import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { EEnrollmentStatus } from '@/shared/api/generated';
import {
  getApiV1CourseregistrationEnrollmentCourseregistrationidDetailsTabQueryKey,
  usePostApiV1CourseregistrationEnrollmentManagementStatus,
} from '@/shared/api/generated';

import type { Enrollment, EnrollmentStatus } from '../types/enrollment';
import { ENROLLMENT_STATUS_TO_CODE } from '../types/enrollmentStatusCode';
import type { StatusForm } from '../ui/organisms/EnrollmentStatusDrawer/EnrollmentStatusDrawer';

/**
 * Hook para alterar o status de gestão de uma matrícula individual.
 * Chama POST /api/v1/courseregistration/enrollment/management-status
 * e invalida a aba de detalhes da matrícula.
 *
 * @param enrollmentId - ID da matrícula
 * @param onSuccess - Callback chamado após alteração bem-sucedida
 */
export function useManagementStatus(enrollmentId: number, onSuccess: () => void) {
  const queryClient = useQueryClient();

  const { mutate, isPending: isSaving } = usePostApiV1CourseregistrationEnrollmentManagementStatus({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey:
            getApiV1CourseregistrationEnrollmentCourseregistrationidDetailsTabQueryKey(
              enrollmentId,
            ),
        });
        onSuccess();
      },
    },
  });

  const changeStatus = useCallback(
    (form: StatusForm, enrollment: Enrollment) => {
      if (!form.newStatus) return;
      mutate({
        data: {
          courseRegistrationId: enrollment.id,
          enrollmentStatus: ENROLLMENT_STATUS_TO_CODE[
            form.newStatus as EnrollmentStatus
          ] as EEnrollmentStatus,
          statusReasonId: form.reasonId ? Number(form.reasonId) : null,
          subStatusReasonId: form.subReasonId ? Number(form.subReasonId) : null,
          observation: form.observation || null,
          sendNotification: form.sendNotification,
          sendCertificateOfCompletionByEmail: form.sendCertificateByEmail,
        },
      });
    },
    [mutate],
  );

  return { changeStatus, isSaving } as const;
}
